/**
 * A document's place in the reader's own repo: reading it back, and writing it.
 *
 * The model is in `#/lib/document/progress`; this is the door to the network, and
 * it is a *query* rather than a bespoke hook because the card is the thing that
 * reads it — the same card that already asks for a document's plays, on the same
 * terms: signed in, on screen, and only for a document with something in it.
 *
 * The place is read wherever the app thinks it stands (see
 * `#/state/queries/document-material` for why that matters here: the PDS being asked
 * is the reader's own, and the app's online state is a health check against a
 * different service entirely).
 *
 * A write that fails is logged by the caller and never waited on. The place is a
 * convenience the *next* visit uses, so a reader who is mid-document must never be
 * held up by it — and the answers themselves are not here at all: they travel as the
 * play (`#/state/queries/document-plays`), which is what the author's report reads.
 */
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
  DOCUMENT_PROGRESS_COLLECTION,
  type DocumentPlace,
  documentPlaceFromRecord,
  documentProgressRecordFor,
  documentProgressRecordKey,
  mergeDocumentPlaces,
} from '#/lib/document/progress'
import {isRecordNotFound} from '#/lib/strings/errors'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

const documentPlaceQueryKeyRoot = 'documentPlace'

export const createDocumentPlaceQueryKey = (
  documentUri: string,
  repo: string | undefined,
) => createQueryKey(documentPlaceQueryKeyRoot, {documentUri, repo})

/**
 * Where this reader got to in a document — `null` when they have no place in it.
 *
 * `null` covers "never opened", "signed out" and "the record is not there", because
 * all three are the same answer to the only question the card has, which is whether
 * there is a place to offer. A request that *failed* is not one of the three, and is
 * thrown rather than cached as an answer.
 */
export function useDocumentPlaceQuery(
  documentUri: string | undefined,
  {enabled = true}: {enabled?: boolean} = {},
) {
  const agent = useAgent()
  const repo = agent.session?.did
  return useQuery({
    queryKey: createDocumentPlaceQueryKey(documentUri ?? '', repo),
    enabled: !!documentUri && !!repo && enabled,
    queryFn: async (): Promise<DocumentPlace | null> => {
      try {
        const res = await agent.com.atproto.repo.getRecord({
          repo: repo!,
          collection: DOCUMENT_PROGRESS_COLLECTION,
          rkey: documentProgressRecordKey(documentUri!),
        })
        return documentPlaceFromRecord(res.data.value, documentUri!)
      } catch (e) {
        if (isRecordNotFound(e)) return null
        throw e
      }
    },
    // A place is written as the reader moves; the cache is refreshed by the write
    // itself (`useSaveDocumentPlace`), so a stale window costs nothing.
    staleTime: 1000 * 30,
    networkMode: 'always',
  })
}

/**
 * Write the reader's place in a document.
 *
 * `putRecord` replaces *or* creates, which is why one call serves a first save and a
 * hundredth, and why nothing here asks whether a record exists first.
 *
 * A place is never *cleared*, and that is a decision rather than a gap: a record is
 * read as a page and two counts, so a stale one costs the next visit a single fetch
 * and is then ignored — `documentResume` offers nothing for a place that has reached
 * the document's last page. Deleting it, on the other hand, would mean deciding *here*
 * that a read is over, and this module knows nothing about questions: a reader who
 * answered the last question on a document they had not finished reading would lose
 * their place to a judgement this layer is not in a position to make.
 *
 * What the caller hands over is *this sitting* — a page turned, a question answered —
 * and what lands is this sitting reconciled with what the record already held
 * (`mergeDocumentPlaces`). That is why the union is made here rather than by the caller:
 * the caller is a render away from the truth and this is the one place every write goes
 * through, so a page seen yesterday cannot be lost to a page seen today by any call site
 * forgetting to remember it. The merged place is left in the cache, because the card is
 * reading it: the count it shows has to move with the count that was written.
 */
export function useSaveDocumentPlace() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      documentUri,
      documentTitle,
      place,
    }: {
      documentUri: string
      documentTitle?: string
      /** `null` writes nothing: a read with no page in it has no place. */
      place: DocumentPlace | null
    }): Promise<void> => {
      const repo = agent.session?.did
      if (!repo || !place) return
      const key = createDocumentPlaceQueryKey(documentUri, repo)
      const remembered = queryClient.getQueryData<DocumentPlace | null>(key)
      const merged = mergeDocumentPlaces(place, remembered)
      const rkey = documentProgressRecordKey(documentUri)
      const record = documentProgressRecordFor(merged, {
        documentUri,
        documentTitle,
      })
      if (!record) return
      await agent.com.atproto.repo.putRecord({
        repo,
        collection: DOCUMENT_PROGRESS_COLLECTION,
        rkey,
        record,
      })
      queryClient.setQueryData(key, merged)
    },
  })
}
